const CATALOG_STATUSES = new Set(['active', 'disabled'])

export const IMAGE_PARAMETER_SCHEMA_OPENAI = 'openai_image'
export const IMAGE_PARAMETER_SCHEMA_GEMINI = 'gemini_image'
export const IMAGE_CAPABILITY_MODE_PASSTHROUGH = 'passthrough'
export const IMAGE_CAPABILITY_MODE_ALLOWLIST = 'allowlist'
export const IMAGE_MAX_OUTPUT_COUNT = 4
export const IMAGE_RESOLUTION_ORDER = Object.freeze(['1K', '2K', '4K'])

export const IMAGE_ASPECT_RATIO_OPTIONS = Object.freeze([
  Object.freeze({ label: '1:1 正方形', value: '1:1' }),
  Object.freeze({ label: '3:4 竖版', value: '3:4' }),
  Object.freeze({ label: '4:3 横版', value: '4:3' }),
  Object.freeze({ label: '9:16 竖屏', value: '9:16' }),
  Object.freeze({ label: '16:9 横屏', value: '16:9' })
])

export const IMAGE_RESOLUTION_OPTIONS = Object.freeze([
  Object.freeze({ label: '标准 1K', value: '1K' }),
  Object.freeze({ label: '高清 2K', value: '2K' }),
  Object.freeze({ label: '超清 4K', value: '4K' })
])

export const IMAGE_OPENAI_SIZE_OPTIONS = Object.freeze([
  Object.freeze({ label: '自动', value: 'auto', tier: '1K' }),
  Object.freeze({ label: '1K 方图 1:1', value: '1024x1024', tier: '1K' }),
  Object.freeze({ label: '1K 横图 3:2', value: '1536x1024', tier: '1K' }),
  Object.freeze({ label: '1K 竖图 2:3', value: '1024x1536', tier: '1K' }),
  Object.freeze({ label: '2K 方图 1:1', value: '2048x2048', tier: '2K' }),
  Object.freeze({ label: '2K 横图 16:9', value: '2048x1152', tier: '2K' }),
  Object.freeze({ label: '4K 横图 16:9', value: '3840x2160', tier: '4K' }),
  Object.freeze({ label: '4K 竖图 9:16', value: '2160x3840', tier: '4K' })
])

// GPT Image 2's asynchronous OpenAI route accepts shape and pixel density as
// separate fields. Keep this list independent from the legacy pixel-size
// list above: mixing a fixed pixel size with an independent tier can produce
// contradictory requests.
export const IMAGE_OPENAI_ASYNC_RATIO_OPTIONS = Object.freeze([
  Object.freeze({ label: '自动', value: 'auto' }),
  Object.freeze({ label: '1:1 正方形', value: '1:1' }),
  Object.freeze({ label: '3:2 横图', value: '3:2' }),
  Object.freeze({ label: '2:3 竖图', value: '2:3' }),
  Object.freeze({ label: '4:3 横图', value: '4:3' }),
  Object.freeze({ label: '3:4 竖图', value: '3:4' }),
  Object.freeze({ label: '5:4 横图', value: '5:4' }),
  Object.freeze({ label: '4:5 竖图', value: '4:5' }),
  Object.freeze({ label: '16:9 宽屏', value: '16:9' }),
  Object.freeze({ label: '9:16 竖屏', value: '9:16' }),
  Object.freeze({ label: '2:1 横幅', value: '2:1' }),
  Object.freeze({ label: '1:2 竖幅', value: '1:2' }),
  Object.freeze({ label: '3:1 超宽', value: '3:1' }),
  Object.freeze({ label: '1:3 超长', value: '1:3' }),
  Object.freeze({ label: '21:9 电影宽屏', value: '21:9' }),
  Object.freeze({ label: '9:21 电影竖屏', value: '9:21' })
])

// Naming aliases make the shared contract convenient for consumers that call
// the first field a ratio or a size. They deliberately point at the same
// frozen option objects and never replace IMAGE_OPENAI_SIZE_OPTIONS.
export const IMAGE_OPENAI_ASYNC_SIZE_OPTIONS = IMAGE_OPENAI_ASYNC_RATIO_OPTIONS
export const IMAGE_OPENAI_ASPECT_RATIO_OPTIONS = IMAGE_OPENAI_ASYNC_RATIO_OPTIONS
export const IMAGE_OPENAI_RATIO_OPTIONS = IMAGE_OPENAI_ASYNC_RATIO_OPTIONS
export const GPT_IMAGE_2_SIZE_OPTIONS = IMAGE_OPENAI_ASYNC_RATIO_OPTIONS

export const IMAGE_OPENAI_RESOLUTION_OPTIONS = IMAGE_RESOLUTION_OPTIONS

export const IMAGE_QUALITY_OPTIONS = Object.freeze([
  Object.freeze({ label: '自动', value: 'auto' }),
  Object.freeze({ label: '低', value: 'low' }),
  Object.freeze({ label: '中', value: 'medium' }),
  Object.freeze({ label: '高', value: 'high' })
])

export const IMAGE_BACKGROUND_OPTIONS = Object.freeze([
  Object.freeze({ label: '自动', value: 'auto' }),
  Object.freeze({ label: '透明', value: 'transparent' }),
  Object.freeze({ label: '不透明', value: 'opaque' })
])

export const IMAGE_FORMAT_OPTIONS = Object.freeze([
  Object.freeze({ label: 'PNG', value: 'png' }),
  Object.freeze({ label: 'WebP', value: 'webp' }),
  Object.freeze({ label: 'JPEG', value: 'jpeg' })
])

export const IMAGE_MODERATION_OPTIONS = Object.freeze([
  Object.freeze({ label: '自动', value: 'auto' }),
  Object.freeze({ label: '低限制', value: 'low' })
])

export const IMAGE_STYLE_OPTIONS = Object.freeze([
  ['无风格', 'none'],
  ['人像摄影', 'portrait'],
  ['电影写真', 'film'],
  ['动漫', 'anime'],
  ['赛博朋克', 'cyberpunk'],
  ['CG 动画', 'cg_animation'],
  ['水墨画', 'ink_painting'],
  ['油画', 'oil_painting'],
  ['风景', 'landscape'],
  ['像素风格', 'pixel_art'],
  ['荧光绘画', 'neon_painting'],
  ['彩铅画', 'colored_pencil'],
  ['儿童绘画', 'children_drawing'],
  ['抽象', 'abstract'],
  ['二次元', '2d_anime'],
  ['莫奈', 'monet_style'],
  ['3D 渲染', '3d_render'],
  ['水彩画', 'watercolor'],
  ['手办', 'figure'],
  ['梵高', 'van_gogh_style']
].map(([label, value]) => Object.freeze({ label, value })))

export class ImageGenerationContractError extends Error {
  constructor(code, message, { path = '' } = {}) {
    super(message)
    this.name = 'ImageGenerationContractError'
    this.code = code
    this.path = path
  }
}

function text(value) {
  return typeof value === 'string' ? value.trim() : ''
}

function isObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function deepFreeze(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value
  Object.values(value).forEach(deepFreeze)
  return Object.freeze(value)
}

function invalid(code, message, path) {
  throw new ImageGenerationContractError(code, message, { path })
}

export function normalizeImageParameterSchema(value) {
  const normalized = text(value).toLowerCase()
  if (['openai', 'openai_image', 'gpt_image', 'gpt-image'].includes(normalized)) {
    return IMAGE_PARAMETER_SCHEMA_OPENAI
  }
  if (['gemini', 'gemini_image', 'nano_banana', 'nano-banana'].includes(normalized)) {
    return IMAGE_PARAMETER_SCHEMA_GEMINI
  }
  return normalized
}

export function inferImageParameterSchema(value = {}, fallback = '') {
  return normalizeImageParameterSchema(
    fallback || value?.parameterSchema || value?.parameter_schema
  )
}

export function normalizeImageCapabilityMode(
  value,
  { fallback = IMAGE_CAPABILITY_MODE_PASSTHROUGH, strict = true, path = 'image_capability_mode' } = {}
) {
  const normalized = text(value).toLowerCase()
  if (!normalized) return fallback
  if ([IMAGE_CAPABILITY_MODE_PASSTHROUGH, IMAGE_CAPABILITY_MODE_ALLOWLIST].includes(normalized)) {
    return normalized
  }
  if (!strict) return fallback
  invalid('unsupported_capability_mode', '图片能力模式不受支持', path)
}

export function normalizeImageResolution(value) {
  const normalized = text(value).toUpperCase().replace(/\s/g, '')
  const aliases = {
    '512': '1K',
    '512PX': '1K',
    '1024': '1K',
    '1024PX': '1K',
    '768': '2K',
    '2048': '2K',
    '2048PX': '2K',
    '3840': '4K',
    '4096': '4K',
    '4096PX': '4K'
  }
  return aliases[normalized] || (IMAGE_RESOLUTION_ORDER.includes(normalized) ? normalized : '')
}

export function normalizeImageAspectRatio(value, { path = 'aspect_ratio' } = {}) {
  const normalized = text(value).replace(/：/g, ':').replace(/\s/g, '')
  if (!/^[1-9]\d*:[1-9]\d*$/.test(normalized)) {
    invalid('invalid_aspect_ratio', '画面比例不正确', path)
  }
  return normalized
}

function normalizeStatus(value, path) {
  const status = text(value).toLowerCase() || 'active'
  if (!CATALOG_STATUSES.has(status)) invalid('invalid_capability_status', '能力状态不受支持', `${path}.status`)
  return status
}

function integer(value, path, { minimum = 0 } = {}) {
  if (!Number.isInteger(value) || value < minimum) {
    invalid('invalid_capability_value', '能力字段类型不正确', path)
  }
  return value
}

/** Normalize one API capability row without retaining secrets or unknown fields. */
export function normalizeImageCapability(value, { path = 'capability' } = {}) {
  if (!isObject(value)) invalid('invalid_capability', '图片规格必须是对象', path)
  const resolutionTier = normalizeImageResolution(value.resolutionTier ?? value.resolution_tier)
  if (!resolutionTier) invalid('invalid_capability_resolution', '清晰度不受支持', `${path}.resolution_tier`)
  const width = value.width
  const height = value.height
  const aspectRatio = normalizeImageAspectRatio(
    value.aspectRatio ?? value.aspect_ratio,
    { path: `${path}.aspect_ratio` }
  )
  return deepFreeze({
    id: text(value.id),
    resolutionTier,
    aspectRatio,
    width: integer(width, `${path}.width`, { minimum: 1 }),
    height: integer(height, `${path}.height`, { minimum: 1 }),
    quality: text(value.quality ?? value.quality_value).toLowerCase(),
    status: normalizeStatus(value.status, path),
    sortOrder: integer(value.sortOrder ?? value.sort_order ?? 0, `${path}.sort_order`)
  })
}

/**
 * Normalize the public Family image capability contract. Candidate rows are
 * intentionally not accepted here: Route capabilities are routing inputs and
 * must never be promoted back into the user-visible Family contract.
 */
export function normalizeImageFamilyCapabilityContract(
  value = {},
  { path = 'family', fallbackMode = IMAGE_CAPABILITY_MODE_ALLOWLIST } = {}
) {
  if (!isObject(value)) invalid('invalid_family', '模型大类必须是对象', path)
  const mode = normalizeImageCapabilityMode(
    value.imageCapabilityMode ?? value.image_capability_mode,
    { fallback: fallbackMode, path: `${path}.image_capability_mode` }
  )
  const rawCapabilities = value.imageCapabilities ?? value.image_capabilities
  if (rawCapabilities !== undefined && rawCapabilities !== null && !Array.isArray(rawCapabilities)) {
    invalid('invalid_capabilities', '模型大类图片能力必须是数组', `${path}.image_capabilities`)
  }
  const imageCapabilities = mergeImageCapabilities(
    ...(Array.isArray(rawCapabilities)
      ? rawCapabilities.map((item, index) => normalizeImageCapability(item, {
        path: `${path}.image_capabilities[${index}]`
      }))
      : [])
  )
  return deepFreeze({
    imageCapabilityMode: mode,
    imageCapabilities
  })
}

function capabilityKey(value) {
  return [
    value.id,
    value.resolutionTier,
    value.aspectRatio,
    value.width,
    value.height,
    value.quality,
    value.status
  ].join('|')
}

export function mergeImageCapabilities(...collections) {
  const seen = new Set()
  return Object.freeze(collections.flat().filter((item) => {
    const key = capabilityKey(item)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  }).sort((left, right) => left.sortOrder - right.sortOrder))
}

export function resolveImageCapabilityMode(routes = []) {
  const values = Array.isArray(routes) ? routes : []
  if (!values.length) return IMAGE_CAPABILITY_MODE_PASSTHROUGH
  return values.some((route) => route.imageCapabilityMode === IMAGE_CAPABILITY_MODE_PASSTHROUGH)
    ? IMAGE_CAPABILITY_MODE_PASSTHROUGH
    : IMAGE_CAPABILITY_MODE_ALLOWLIST
}

function activeCapabilities(family) {
  const values = Array.isArray(family?.capabilities)
    ? family.capabilities
    : (Array.isArray(family?.imageCapabilities) ? family.imageCapabilities : [])
  return values.filter((item) => {
    const status = text(item?.status).toLowerCase()
    return !status || status === 'active'
  })
}

function option(value, label, disabled = false) {
  return Object.freeze({ value, label, disabled })
}

function chooseValue(options, preferred, fallback = '') {
  const enabled = options.filter((item) => !item.disabled)
  return enabled.some((item) => item.value === preferred)
    ? preferred
    : (enabled[0]?.value || fallback)
}

function normalizeStyle(value) {
  const normalized = text(value) || 'none'
  return IMAGE_STYLE_OPTIONS.some((item) => item.value === normalized) ? normalized : 'none'
}

export function normalizeImageOutputCount(value, rights = {}, maxOutputCount = IMAGE_MAX_OUTPUT_COUNT) {
  const configured = Number(value)
  const maximum = rights?.maxConcurrency > 0
    ? Math.min(rights.maxConcurrency, maxOutputCount)
    : maxOutputCount
  if (!Number.isInteger(configured) || configured < 1) return 1
  return Math.min(configured, maximum)
}

export function normalizeImageSelection(preferred = {}, rights = {}, { maxOutputCount = IMAGE_MAX_OUTPUT_COUNT } = {}) {
  const preferredAspectRatio = text(preferred?.aspectRatio ?? preferred?.aspect_ratio)
  const preferredImageSize = preferred?.imageSize ?? preferred?.image_size
  const preferredResolution = preferred?.resolution ?? preferred?.resolutionTier ??
    preferred?.resolution_tier
  const imageSize = normalizeImageResolution(preferredImageSize) ||
    normalizeImageResolution(preferredResolution) || '1K'
  const resolution = normalizeImageResolution(preferredResolution) || imageSize
  return {
    aspectRatio: preferredAspectRatio || '1:1',
    imageSize,
    // OpenAI calls the ratio `size`; Gemini calls it `aspect_ratio`.
    // Keep both aliases in the normalized selection so every consumer can use
    // the same Family capability tuple.
    resolution,
    size: text(preferred?.size) || preferredAspectRatio || 'auto',
    quality: text(preferred?.quality).toLowerCase() || 'auto',
    background: text(preferred?.background).toLowerCase() || 'auto',
    outputFormat: text(preferred?.outputFormat).toLowerCase() || 'png',
    outputCount: normalizeImageOutputCount(preferred?.outputCount, rights, maxOutputCount),
    moderation: text(preferred?.moderation).toLowerCase() || 'auto',
    style: normalizeStyle(preferred?.style)
  }
}

function resolutionAllowed(rights, value) {
  const allowed = Array.isArray(rights?.imageResolutions)
    ? rights.imageResolutions.map(normalizeImageResolution).filter(Boolean)
    : []
  return !allowed.length || allowed.includes(normalizeImageResolution(value))
}

function capabilityResolution(capability) {
  return normalizeImageResolution(
    capability?.resolutionTier ?? capability?.resolution_tier
  )
}

function capabilityAspectRatio(capability) {
  return text(capability?.aspectRatio ?? capability?.aspect_ratio)
    .replace(/：/g, ':')
    .replace(/\s/g, '')
}

function capabilityQuality(capability) {
  return text(capability?.quality ?? capability?.qualityValue ?? capability?.quality_value)
    .toLowerCase()
}

const ASPECT_RATIO_LABELS = new Map([
  ...IMAGE_ASPECT_RATIO_OPTIONS,
  ...IMAGE_OPENAI_ASYNC_RATIO_OPTIONS
].map((item) => [item.value, item.label]))
const RESOLUTION_LABELS = new Map(IMAGE_RESOLUTION_OPTIONS.map((item) => [item.value, item.label]))
const QUALITY_LABELS = new Map(IMAGE_QUALITY_OPTIONS.map((item) => [item.value, item.label]))

function uniqueCapabilityValues(capabilities, reader) {
  const seen = new Set()
  const values = []
  capabilities.forEach((capability) => {
    const value = reader(capability)
    if (!value || seen.has(value)) return
    seen.add(value)
    values.push(value)
  })
  return values
}

function capabilityMatches(capability, { aspectRatio = '', quality = '', resolution = '' } = {}) {
  if (aspectRatio && capabilityAspectRatio(capability) !== aspectRatio) return false
  if (resolution && capabilityResolution(capability) !== resolution) return false
  const expectedQuality = text(quality).toLowerCase()
  const configuredQuality = capabilityQuality(capability)
  return !expectedQuality || expectedQuality === 'auto' || !configuredQuality ||
    configuredQuality === expectedQuality
}

function familyCapabilityFormOptions(family, rights, preferred, schema) {
  const capabilities = activeCapabilities(family)
  if (!capabilities.length) {
    return {
      qualityOptions: [],
      ratioOptions: [],
      resolutionOptions: [],
      selection: {
        ...preferred,
        aspectRatio: '',
        imageSize: '',
        quality: '',
        resolution: '',
        size: ''
      },
      sizeOptions: []
    }
  }

  const ratios = uniqueCapabilityValues(capabilities, capabilityAspectRatio)
  const resolutions = uniqueCapabilityValues(capabilities, capabilityResolution)
  const configuredQualities = uniqueCapabilityValues(capabilities, capabilityQuality)
  const hasWildcardQuality = capabilities.some((capability) => !capabilityQuality(capability))
  const qualityValues = schema === IMAGE_PARAMETER_SCHEMA_OPENAI
    ? IMAGE_QUALITY_OPTIONS.map((item) => item.value).filter((value) => (
        value === 'auto' || hasWildcardQuality || configuredQualities.includes(value)
      ))
    : []

  const ratioOptionsFor = (resolution, quality) => ratios.map((value) => option(
    value,
    ASPECT_RATIO_LABELS.get(value) || value,
    !capabilities.some((capability) => (
      resolutionAllowed(rights, capabilityResolution(capability)) &&
      capabilityMatches(capability, { aspectRatio: value, quality, resolution })
    ))
  ))
  const resolutionOptionsFor = (aspectRatio, quality) => resolutions.map((value) => option(
    value,
    RESOLUTION_LABELS.get(value) || value,
    !resolutionAllowed(rights, value) || !capabilities.some((capability) => (
      capabilityMatches(capability, { aspectRatio, quality, resolution: value })
    ))
  ))
  const qualityOptionsFor = (aspectRatio, resolution) => qualityValues.map((value) => option(
    value,
    QUALITY_LABELS.get(value) || value,
    !capabilities.some((capability) => (
      resolutionAllowed(rights, capabilityResolution(capability)) &&
      capabilityMatches(capability, { aspectRatio, quality: value, resolution })
    ))
  ))

  const preferredRatio = text(schema === IMAGE_PARAMETER_SCHEMA_OPENAI
    ? (preferred.size || preferred.aspectRatio)
    : (preferred.aspectRatio || preferred.size)
  ).replace(/：/g, ':').replace(/\s/g, '')
  const preferredResolution = normalizeImageResolution(schema === IMAGE_PARAMETER_SCHEMA_OPENAI
    ? (preferred.resolution || preferred.imageSize)
    : (preferred.imageSize || preferred.resolution)
  )
  let ratioOptions = ratioOptionsFor(preferredResolution, preferred.quality)
  let ratio = chooseValue(ratioOptions, preferredRatio, '')
  let resolutionOptions = resolutionOptionsFor(ratio, preferred.quality)
  let resolution = chooseValue(
    resolutionOptions,
    preferredResolution,
    ''
  )
  let qualityOptions = qualityOptionsFor(ratio, resolution)
  let quality = schema === IMAGE_PARAMETER_SCHEMA_OPENAI
    ? chooseValue(qualityOptions, text(preferred.quality).toLowerCase(), '')
    : 'auto'

  ratioOptions = ratioOptionsFor(resolution, quality)
  ratio = chooseValue(ratioOptions, ratio, '')
  resolutionOptions = resolutionOptionsFor(ratio, quality)
  resolution = chooseValue(resolutionOptions, resolution, '')
  qualityOptions = qualityOptionsFor(ratio, resolution)
  if (schema === IMAGE_PARAMETER_SCHEMA_OPENAI) {
    quality = chooseValue(qualityOptions, quality, '')
  }

  return {
    qualityOptions,
    ratioOptions,
    resolutionOptions,
    selection: {
      ...preferred,
      aspectRatio: ratio,
      imageSize: resolution,
      quality,
      resolution,
      size: ratio
    },
    sizeOptions: ratioOptions
  }
}

function schemaForFamily(family) {
  const schema = normalizeImageParameterSchema(
    family?.parameterSchema ?? family?.parameter_schema
  )
  return [IMAGE_PARAMETER_SCHEMA_GEMINI, IMAGE_PARAMETER_SCHEMA_OPENAI].includes(schema)
    ? schema
    : ''
}

export function resolveImageGenerationFormOptions(
  family,
  rights = {},
  preferred = {},
  { maxOutputCount = IMAGE_MAX_OUTPUT_COUNT } = {}
) {
  const selection = normalizeImageSelection(preferred, rights, { maxOutputCount })
  const schema = schemaForFamily(family)
  const schemaOptions = schema
    ? familyCapabilityFormOptions(family, rights, selection, schema)
    : familyCapabilityFormOptions(null, rights, selection, schema)
  const maximum = rights?.maxConcurrency > 0
    ? Math.min(rights.maxConcurrency, maxOutputCount)
    : maxOutputCount
  return deepFreeze({
    schema,
    selection: {
      ...schemaOptions.selection,
      background: chooseValue(IMAGE_BACKGROUND_OPTIONS, selection.background, 'auto'),
      outputFormat: chooseValue(IMAGE_FORMAT_OPTIONS, selection.outputFormat, 'png'),
      moderation: chooseValue(IMAGE_MODERATION_OPTIONS, selection.moderation, 'auto')
    },
    ratioOptions: schemaOptions.ratioOptions || [],
    resolutionOptions: schemaOptions.resolutionOptions || [],
    sizeOptions: schemaOptions.sizeOptions || [],
    qualityOptions: schemaOptions.qualityOptions || [],
    backgroundOptions: IMAGE_BACKGROUND_OPTIONS,
    formatOptions: IMAGE_FORMAT_OPTIONS,
    moderationOptions: IMAGE_MODERATION_OPTIONS,
    outputCountOptions: Array.from({ length: maxOutputCount }, (_, index) => (
      option(index + 1, `${index + 1} 张`, index + 1 > maximum)
    )),
    styleOptions: IMAGE_STYLE_OPTIONS
  })
}
